/**
 * +presence: shared-field presence client (docs/design/multiplayer.md §10, zones.md "Shared fields").
 *
 *   startFieldPresence({ zoneId, fieldId, heroName, classId, hudHost, getLocalState })
 *   presenceEvent({ k: 'jump' })      // local cast start / finish / cancel, jump, level-up, emote
 *   forwardSimEvents(events, world, heroId)   // the same from sim events, and waystone travel
 *   stopFieldPresence()
 *
 * One WebSocket to the field's FieldShard (the directory picks the shard; party members prefer
 * the same one). Sends the hero's state at FIELD_SEND_HZ while it changes (a keepalive every
 * FIELD_KEEPALIVE_MS otherwise), reconnects with backoff, and keeps the others on a timeline
 * INTERP_MS in the past (net/presenceModel.ts). The session draws them through `presenceHook`
 * (render/toon/remotePlayers.ts); zone chat goes into the HUD chat frame and speech bubbles.
 *
 * Never connects offline (`?offline=1`, no server), in the tutorial, or in a dungeon: the world
 * layer only starts it on an online field / town.
 */
import type { ContentRegistry, DeepReadonly, SimEvent, UnitId, World } from '@keystone/sim';
import { AUTO_SHOT_ABILITY_ID, MELEE_ABILITY_ID } from '@keystone/sim';
import { OFFHAND_CUE_ID } from '../game/frame'; // +autoattack
import type { ChatLine } from '../hud/chat/model';
import type { RenderFrame, Renderer } from '../render/types';
import { DEVICE_TOKEN_KEY } from '../meta/api';
import { RemotePlayers, remoteUnitId } from '../render/toon/remotePlayers';
import { NET_BUILD } from './protocol';
import { type FieldClientMsg, type FieldEventIn, type FieldServerMsg, type FieldState, AN_AIR, AN_CAST, AN_CHANNEL, AN_DEAD, AN_GLIDE, FIELD_CLOSE, FIELD_KEEPALIVE_MS, FIELD_SEND_HZ } from './fieldProtocol';
import { PresenceModel } from './presenceModel';
import { type PresenceSessionHook, presenceHook } from './presenceHook';

/** What the world layer reports about the local hero (sim state; read every send tick). */
export interface LocalPresenceState {
  x: number;
  y: number;
  facing: number;
  vx: number;
  vy: number;
  /** Casting or channelling ability (null = none). */
  cast: { abilityId: string; channel: boolean } | null;
  airborne: boolean;
  /** +glide: the glider is open; the absolute height in the air / on a platform (null on the ground). */
  gliding?: boolean;
  z?: number | null;
  dead: boolean;
  /** The current target's broad kind (null = none). */
  targetKind: 'e' | 'f' | null;
}

export type PresenceNotice =
  | { type: 'status'; status: 'connecting' | 'open' | 'closed'; shard?: number; players?: number }
  | { type: 'join'; name: string }
  | { type: 'leave'; name: string }
  | { type: 'error'; code: string; msg: string };

export interface PresenceOptions {
  zoneId: string;
  fieldId: string;
  /** The local hero's name and class (own zone chat lines). */
  heroName: string;
  classId: string;
  hudHost: HTMLElement;
  getLocalState(): LocalPresenceState | null;
  onEvent?(e: PresenceNotice): void;
  /** Test / tools override of the socket URL. */
  url?: string;
}

const MAX_BACKOFF_MS = 10_000;
/** Party code kept by main.ts for reloads (a party prefers one shard). */
const PARTY_KEY = 'keystone.party';

class FieldPresence implements PresenceSessionHook {
  readonly model = new PresenceModel();
  private ws: WebSocket | null = null;
  private retry = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private sendTimer: ReturnType<typeof setInterval> | null = null;
  private stopped = false;
  /** Closed for idling (hidden 15 min): reconnect when the tab is visible again. */
  private idleClosed = false;
  private lastSent: FieldState | null = null;
  private lastSentAt = 0;
  private chat: ChatLine[] = [];
  private renderer: RemotePlayers | null = null;
  private content: ContentRegistry | null = null;
  private status: 'connecting' | 'open' | 'closed' = 'connecting';
  private readonly onVisible = (): void => {
    if (!document.hidden && this.idleClosed && !this.stopped) {
      this.idleClosed = false;
      this.connect();
    }
  };

  constructor(private readonly opts: PresenceOptions) {
    document.addEventListener('visibilitychange', this.onVisible);
    this.sendTimer = setInterval(() => this.sendState(), Math.round(1000 / FIELD_SEND_HZ));
    this.connect();
  }

  // ------------------------------------------------------------------ socket

  private url(): string | null {
    if (this.opts.url) return this.opts.url;
    let token: string | null = null;
    let party: string | null = null;
    try {
      token = localStorage.getItem(DEVICE_TOKEN_KEY);
      party = sessionStorage.getItem(PARTY_KEY);
    } catch {
      /* no storage: no token */
    }
    if (!token) return null;
    const origin = location.origin.replace(/^http/, 'ws');
    const q = `token=${encodeURIComponent(token)}${party ? `&party=${encodeURIComponent(party)}` : ''}`;
    return `${origin}/api/field/${encodeURIComponent(this.opts.zoneId)}/${encodeURIComponent(this.opts.fieldId)}/ws?${q}`;
  }

  private connect(): void {
    if (this.stopped) return;
    const url = this.url();
    if (!url) return;
    this.setStatus('connecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.scheduleRetry();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.retry = 0;
      this.lastSent = null;
      this.send({ t: 'hello', build: NET_BUILD });
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws || typeof ev.data !== 'string') return;
      let msg: FieldServerMsg;
      try {
        msg = JSON.parse(ev.data) as FieldServerMsg;
      } catch {
        return;
      }
      this.onMessage(msg);
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.model.reset();
      this.setStatus('closed');
      if (this.stopped) return;
      if (ev.code === FIELD_CLOSE.replaced) return; // another tab took over
      if (ev.code === FIELD_CLOSE.idle) {
        this.idleClosed = true;
        if (!document.hidden) this.onVisible();
        return;
      }
      this.scheduleRetry();
    };
  }

  private scheduleRetry(): void {
    if (this.stopped || this.retryTimer) return;
    const base = Math.min(MAX_BACKOFF_MS, 500 * 2 ** this.retry++);
    this.retryTimer = setTimeout(
      () => {
        this.retryTimer = null;
        this.connect();
      },
      base * (0.75 + Math.random() * 0.5),
    );
  }

  private send(msg: FieldClientMsg): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  private onMessage(msg: FieldServerMsg): void {
    const now = performance.now();
    if (msg.t === 'error') {
      this.opts.onEvent?.({ type: 'error', code: msg.code, msg: msg.msg });
      // A full shard: the directory picks another one on the retry.
      if (msg.code === 'shard_full') this.retry = 0;
      else if (msg.code === 'bad_field' || msg.code === 'no_character') this.stopped = true;
      return;
    }
    const r = this.model.apply(msg, now);
    if (msg.t === 'welcome') this.setStatus('open');
    if (r.joined) this.opts.onEvent?.({ type: 'join', name: r.joined.name });
    if (r.left) this.opts.onEvent?.({ type: 'leave', name: r.left.name });
  }

  private setStatus(s: 'connecting' | 'open' | 'closed'): void {
    this.status = s;
    this.opts.onEvent?.({ type: 'status', status: s, ...(s === 'open' ? { shard: this.model.shard, players: this.model.players.size + 1 } : {}) });
  }

  // ------------------------------------------------------------------ local state / events

  private sendState(): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    let l: LocalPresenceState | null = null;
    try {
      l = this.opts.getLocalState();
    } catch {
      l = null;
    }
    if (!l) return;
    const q = (v: number): number => Math.round(v * 100) / 100;
    const an = (l.cast ? (l.cast.channel ? AN_CHANNEL : AN_CAST) : 0) | (l.airborne ? AN_AIR : 0) | (l.dead ? AN_DEAD : 0) | (l.gliding ? AN_GLIDE : 0);
    const vis: 0 | 1 | 2 = document.hidden ? 2 : document.hasFocus() ? 0 : 1;
    const s: FieldState = { x: q(l.x), y: q(l.y), f: q(l.facing), vx: q(l.vx), vy: q(l.vy), an };
    if (l.cast) s.c = l.cast.abilityId;
    if (l.targetKind) s.tk = l.targetKind;
    if (vis) s.vis = vis;
    if (typeof l.z === 'number' && Number.isFinite(l.z)) s.z = q(l.z); // +glide
    const p = this.lastSent;
    const now = performance.now();
    const same = !!p && p.x === s.x && p.y === s.y && p.f === s.f && p.vx === s.vx && p.vy === s.vy && p.an === s.an && p.c === s.c && p.tk === s.tk && p.vis === s.vis && p.z === s.z;
    if (same && now - this.lastSentAt < FIELD_KEEPALIVE_MS) return;
    this.lastSent = s;
    this.lastSentAt = now;
    this.send({ t: 's', ...s });
  }

  event(e: FieldEventIn): void {
    // The state rides along first so the event lands where the hero is.
    this.sendState();
    this.send({ t: 'e', ...e });
  }

  /**
   * +presence: a waystone jump. The travel event goes BEFORE the state from the new place: the
   * shard validates it (a real stone of this field, arriving near it, from near a stone, off
   * cooldown) and snaps the hero there; the state that follows is then a zero-length move. (State
   * first would be a teleport to the movement budget: the others would see a slide.)
   */
  travel(waystoneId: string, to: { x: number; y: number }): void {
    const q = (v: number): number => Math.round(v * 100) / 100;
    this.send({ t: 'e', k: 'travel', w: waystoneId, x: q(to.x), y: q(to.y) });
    this.lastSent = null;
    this.sendState();
  }

  // ------------------------------------------------------------------ session hook

  decorate(frame: RenderFrame, content: ContentRegistry, now: number): void {
    this.content = content;
    if (!this.renderer) this.renderer = new RemotePlayers(this.opts.hudHost);
    const t = this.model.renderTime(now);
    const due = this.model.due(t);
    for (const e of due) {
      if (e.k !== 'chat') continue;
      const p = this.model.players.get(e.id);
      if (!p) continue;
      this.chat.push({ kind: 'say', unitId: remoteUnitId(e.id), name: p.name, color: content.class(p.cls)?.color ?? '#e6e9ef', text: e.text, channel: 'Zone' });
    }
    this.renderer.decorate(frame, content, this.model, t, due);
    this.model.trim(t - 1000);
  }

  drainChat(): ChatLine[] {
    if (!this.chat.length) return EMPTY;
    const out = this.chat;
    this.chat = [];
    return out;
  }

  overlay(renderer: Renderer, focus: { x: number; y: number }, _now: number): void {
    this.renderer?.overlay(renderer, focus, this.model, this.content);
  }

  say(text: string, playerId: UnitId): boolean {
    if (this.status !== 'open') return false;
    this.send({ t: 'chat', text });
    this.chat.push({ kind: 'say', unitId: playerId, name: this.opts.heroName, color: this.content?.class(this.opts.classId)?.color ?? '#e6e9ef', text, channel: 'Zone' });
    return true;
  }

  stop(): void {
    this.stopped = true;
    document.removeEventListener('visibilitychange', this.onVisible);
    if (this.sendTimer) clearInterval(this.sendTimer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    const ws = this.ws;
    this.ws = null;
    try {
      ws?.close(1000, 'left the field');
    } catch {
      /* closed */
    }
    this.renderer?.dispose();
    this.renderer = null;
    this.model.reset();
  }
}

const EMPTY: ChatLine[] = [];
let active: FieldPresence | null = null;

/** Join a field's shared presence (replaces any previous one). */
export function startFieldPresence(opts: PresenceOptions): void {
  stopFieldPresence();
  active = new FieldPresence(opts);
  presenceHook.current = active;
  if (import.meta.env?.DEV || location.search.includes('debug')) (globalThis as { __presence?: unknown }).__presence = active;
}

/** Leave the field's presence (map change, dungeon, menus). */
export function stopFieldPresence(): void {
  if (!active) return;
  if (presenceHook.current === active) presenceHook.current = null;
  active.stop();
  active = null;
}

/** A local event (cast start / finish / cancel, jump, level-up, emote) for the others to see. */
export function presenceEvent(e: FieldEventIn): void {
  active?.event(e);
}

export function fieldPresenceActive(): boolean {
  return active !== null;
}

/**
 * The world layer's `getLocalState`: the hero's sim state (velocity measured between reads).
 * `getWorld` returns null when the map is gone.
 */
export function simLocalState(getWorld: () => DeepReadonly<World> | null, heroId: UnitId): () => LocalPresenceState | null {
  let prev: { x: number; y: number; t: number } | null = null;
  let vx = 0;
  let vy = 0;
  return () => {
    const w = getWorld();
    const u = w?.units[heroId];
    if (!w || !u) return null;
    if (prev && w.time > prev.t) {
      const dt = (w.time - prev.t) / 1000;
      vx = (u.pos.x - prev.x) / dt;
      vy = (u.pos.y - prev.y) / dt;
    } else if (!prev) vx = vy = 0;
    if (!prev || w.time !== prev.t) prev = { x: u.pos.x, y: u.pos.y, t: w.time };
    const tgt = u.targetId ? w.units[u.targetId] : undefined;
    return {
      x: u.pos.x,
      y: u.pos.y,
      facing: u.facing,
      vx,
      vy,
      cast: u.cast ? { abilityId: u.cast.abilityId, channel: u.cast.kind === 'channel' } : null,
      airborne: !!u.jump && (!!u.jump.air || w.time < u.jump.until),
      gliding: !!u.jump?.air?.glide, // +glide
      z: u.jump?.air?.z ?? u.perch?.z ?? null,
      dead: !u.alive,
      targetKind: tgt ? (tgt.faction === 'enemy' ? 'e' : 'f') : null,
    };
  };
}

/** The hero's sim events the others should see (casts, melee swings, jumps, level-ups), in order. */
export function forwardSimEvents(events: readonly SimEvent[], world: DeepReadonly<World>, heroId: UnitId): void {
  if (!active) return;
  const u = world.units[heroId];
  for (const e of events) {
    if (e.sourceID !== heroId) continue;
    switch (e.type) {
      case 'begincast':
        active.event({ k: 'cs', a: e.abilityGameID, d: e.duration });
        break;
      case 'cast': {
        const t = e.targetID ? world.units[e.targetID] : undefined;
        const p = t?.pos ?? u?.cast?.point ?? (u?.targetId ? world.units[u.targetId]?.pos : undefined);
        active.event({ k: 'cf', a: e.abilityGameID, ...(p ? { tp: [p.x, p.y] as [number, number] } : {}) });
        break;
      }
      case 'damage':
        // +autoattack: white swings (off hand as its own cue) and Auto Shot arrows.
        if ((e.abilityGameID === MELEE_ABILITY_ID || e.abilityGameID === AUTO_SHOT_ABILITY_ID) && !e.tick) {
          const t = world.units[e.targetID];
          const a = e.abilityGameID === AUTO_SHOT_ABILITY_ID ? AUTO_SHOT_ABILITY_ID : e.swing === 'oh' ? OFFHAND_CUE_ID : MELEE_ABILITY_ID;
          active.event({ k: 'cf', a, ...(t ? { tp: [t.pos.x, t.pos.y] as [number, number] } : {}) });
        }
        break;
      case 'cast_cancelled':
        active.event({ k: 'cx' });
        break;
      case 'jump':
        active.event({ k: 'jump' });
        break;
      case 'waystone': // +presence: validated travel (snap + poof for the others), not a budget slide
        active.travel(e.waystoneId, e.to);
        break;
      case 'level_up':
        active.event({ k: 'lvl' });
        break;
      default:
        break;
    }
  }
}
